/**
 * STORY-006-FUP-1 — config-driven filter renderer types.
 *
 * `FilterSpec` describes WHICH filters the sidebar renders and in what order.
 * `FacetResponse` carries the live counts from `/api/v1/products/facets`.
 *
 * These two are passed into `<ProductFilters>` together. The component
 * has no hidden state beyond what's in the URL and doesn't call out to
 * decide what to show — all input is explicit.
 */
import type { CategoryFilterConfig, CategoryFilterKey } from '../../../types'

// ─── FilterSpec ──────────────────────────────────────────────────────────────
// Discriminated union over the known filter kinds. The built-in `kind`s
// mirror `CategoryFilterKey` 1:1 so a per-category `filter_config` coming
// from `CategoryResource` maps directly (see `specFromCategoryConfig`).
//
// `attribute` is the per-category extension: it renders a checkbox list
// backed by `Product.filterable_attributes[attributeKey]` and the facet
// counts under `FacetResponse.attribute_facets[attributeKey]`. The
// backend ships these as `filters[]` entries with the
// `{ kind: 'attribute', attributeKey, label }` shape — see
// `CategoryFilterConfigEntry` in `src/types/index.ts`.

export type FilterSpecEntry =
  | { kind: 'category' }
  | { kind: 'subcategory' }
  | { kind: 'series' }
  | { kind: 'brand' }
  | { kind: 'price_range' }
  | { kind: 'stock_status' }
  | { kind: 'delivery_speed' }
  | { kind: 'lifecycle_phase' }
  | { kind: 'legacy' }
  | { kind: 'labels' }
  | { kind: 'attribute'; attributeKey: string; label: string }

export interface FilterSpec {
  /** Filter sections, rendered in array order. */
  filters: FilterSpecEntry[]
}

/**
 * Default section order used on `/nl/products` (no category selected)
 * and as the fall-back when a category has no `filter_config`.
 *
 * `category` leads — removing it would orphan the sidebar (users can't
 * drill down any more). Keep it first unconditionally.
 */
export const DEFAULT_FILTER_SPEC: FilterSpec = {
  filters: [
    { kind: 'category' },
    { kind: 'price_range' },
    { kind: 'stock_status' },
    { kind: 'delivery_speed' },
    { kind: 'brand' },
    { kind: 'series' },
    { kind: 'lifecycle_phase' },
    { kind: 'legacy' },
    { kind: 'labels' },
  ],
}

const DEFAULT_KINDS: CategoryFilterKey[] = DEFAULT_FILTER_SPEC.filters.map(
  (f) => f.kind,
) as CategoryFilterKey[]

/**
 * Build a `FilterSpec` from a category's `filter_config` (the JSON shape
 * Symfony ships on `CategoryResource.filter_config`).
 *
 *  - When `config.filters` is present: use exactly those entries, sorted
 *    by `order` (ascending). Each entry is either a built-in
 *    `{ key, order? }` (mapped to `FilterSpecEntry.kind`) or an
 *    `{ kind: 'attribute', attributeKey, label, order? }`. `category` is
 *    prepended when missing from the built-ins.
 *  - When only `config.hiddenFilters` is present: start from the defaults,
 *    remove every hidden key, but never drop `category`.
 *  - When `config` is `null`/`undefined`: return `DEFAULT_FILTER_SPEC`.
 *
 * Duplicates are dropped (first occurrence wins). For built-in kinds the
 * dedupe key is `kind`; for `attribute` entries it is `attribute:{attributeKey}`
 * so the same attribute is never rendered twice but two different
 * attributes coexist.
 */
export function specFromCategoryConfig(
  config: CategoryFilterConfig | null | undefined,
): FilterSpec {
  if (!config) return DEFAULT_FILTER_SPEC

  if (config.filters && config.filters.length > 0) {
    const sorted = [...config.filters].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    )
    const entries: FilterSpecEntry[] = sorted.map((f) => {
      // Discriminate positively on the built-in variant (`key`). TS narrows
      // the `in`-operator else-branch to the attribute variant cleanly this
      // way; the previous `'kind' in f && ...` form left `f` ambiguous.
      if ('key' in f) {
        return { kind: f.key }
      }
      return { kind: 'attribute', attributeKey: f.attributeKey, label: f.label }
    })
    // Ensure `category` is present — the sidebar would otherwise have no
    // way to drill down.
    if (!entries.some((e) => e.kind === 'category')) {
      entries.unshift({ kind: 'category' })
    }
    return { filters: dedupeEntries(entries) }
  }

  const hidden = new Set(config.hiddenFilters ?? [])
  const keep = DEFAULT_KINDS.filter((k) => k === 'category' || !hidden.has(k))
  return { filters: keep.map((kind) => ({ kind }) as FilterSpecEntry) }
}

function dedupeEntries(items: FilterSpecEntry[]): FilterSpecEntry[] {
  const seen = new Set<string>()
  const out: FilterSpecEntry[] = []
  for (const it of items) {
    const key = it.kind === 'attribute' ? `attribute:${it.attributeKey}` : it.kind
    if (seen.has(key)) continue
    seen.add(key)
    out.push(it)
  }
  return out
}

/**
 * @deprecated Pre-STORY-006-FUP-1 helper — returns just the ordered list
 * of built-in filter kinds. Retained so callers migrating incrementally
 * still compile. New code should use `specFromCategoryConfig` and work
 * with the full `FilterSpec`. Attribute filters (`kind === 'attribute'`)
 * are dropped from the output — this helper has no way to represent them.
 */
export function resolveFilterOrder(
  config: CategoryFilterConfig | null | undefined,
): CategoryFilterKey[] {
  return specFromCategoryConfig(config)
    .filters.filter((f) => f.kind !== 'attribute')
    .map((f) => f.kind) as CategoryFilterKey[]
}

// ─── FacetResponse ───────────────────────────────────────────────────────────
// Mirrors the backend's `ProductFacetsResource` after the
// `WrappedResponseNormalizer` has converted camelCase → snake_case.
// See backend/src/Catalog/Api/Resource/ProductFacetsResource.php and
// backend/src/Catalog/Api/Provider/ProductFacetsProvider.php.

export interface FacetBucket {
  value: string
  count: number
}

export interface PriceBucket {
  min: number
  /** Open-ended upper bucket has `max = null`. */
  max: number | null
  count: number
  /**
   * Pre-formatted Dutch-euro label from the backend (e.g. "€ 100 – € 500",
   * "€ 1k+", "Alles"). Round-tripped as-is — do NOT re-derive client-side.
   */
  label: string
}

export interface FacetResponse {
  total: number
  categories: FacetBucket[]
  subcategories: FacetBucket[]
  series: FacetBucket[]
  brands: FacetBucket[]
  stock_statuses: FacetBucket[]
  lifecycle_phases: FacetBucket[]
  delivery_speeds: FacetBucket[]
  labels: FacetBucket[]
  price_buckets: PriceBucket[]
  /**
   * Per-category attribute facets: a map from the raw
   * `filterable_attributes` key (e.g. "Number of channels") to the value
   * buckets observed in the current filtered product set. Optional — the
   * backend only emits this when the active category's `filter_config`
   * declares attribute filters.
   *
   * Wire key is snake_case (`attribute_facets`) to match the rest of this
   * response (`stock_statuses`, `price_buckets`, etc.) — API Platform
   * serialises the backend PHP property `attributeFacets` to snake_case.
   */
  attribute_facets?: Record<string, FacetBucket[]>
}
